// COP — Public pricing page (Wave 2C.2).
//
// Renders the live `public.plans` catalog filtered to
// `is_active = true AND show_on_pricing_page = true`, grouped into:
//   * B2B Planner tiles (Starter / Pro / Studio) with a Mensual ⇄ Anual toggle.
//   * B2C "Para parejas y familias" section (Free + Plus, with a 6-month
//     one-time toggle on the Plus card per locked decision #4).
//
// CTA wiring:
//   * Free / trial slugs ('trial', 'b2c-free') never hit Stripe. Signed-out
//     callers go to `/signup?plan=<slug>`; signed-in callers see a disabled
//     "Plan actual" or "Iniciar prueba gratis" depending on state.
//   * Paid slugs invoke the `createCheckoutSession` edge function. When the
//     caller is signed out we route to `/login?next=/precios?plan=<slug>`
//     first so they land back here after auth.
//
// Locked decisions honored:
//   * Decision #1: Trial slug = 'trial', 15 días, card-less. The trial tile
//     is implicit — the Free / "Iniciar prueba" path on the Planner column.
//   * Decision #4: 'b2c-plus-6mo' uses the same checkout endpoint; the edge
//     function picks mode:'payment'.
//   * Decision #7: payment_method_types: ['card'] is enforced server-side.

import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { PublicNavbar } from '@/components/Layout/PublicNavbar';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { Plan, CheckoutSessionResponse } from '@/types';

const PRIMARY_COLOR = '#3d5a80';

const mxnFormatter = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  maximumFractionDigits: 0,
});

/** Format `price_mxn_cents` as a MXN string with no decimals. */
function formatMxn(cents: number): string {
  return mxnFormatter.format(cents / 100);
}

// ---------------------------------------------------------------------------
// Data fetch — public, anonymous-readable filtered view.
// RLS already restricts callers to is_active rows; we narrow further to the
// pricing-page subset and order by sort_order so the super admin's column
// order in `/app/superadmin/plans` matches what visitors see.
// ---------------------------------------------------------------------------

async function fetchPublicPlans(): Promise<Plan[]> {
  const client = supabase as unknown as {
    from: (t: string) => {
      select: (cols: string) => {
        eq: (col: string, value: unknown) => {
          eq: (col: string, value: unknown) => {
            order: (col: string, opts: { ascending: boolean }) => Promise<{
              data: Plan[] | null;
              error: unknown;
            }>;
          };
        };
      };
    };
  };
  const { data, error } = await client
    .from('plans')
    .select('*')
    .eq('is_active', true)
    .eq('show_on_pricing_page', true)
    .order('sort_order', { ascending: true });
  if (error) {
    console.error('Pricing: failed to load plans', error);
    throw error;
  }
  return data ?? [];
}

// ---------------------------------------------------------------------------
// Grouping helpers — slug-based, because slugs are stable contracts (see
// decisions table). Anything not in the known lists falls through silently.
// ---------------------------------------------------------------------------

const PLANNER_MONTHLY_SLUGS = [
  'planner-starter-month',
  'planner-pro-month',
  'planner-studio-month',
] as const;

const PLANNER_YEARLY_SLUGS = [
  'planner-starter-year',
  'planner-pro-year',
  'planner-studio-year',
] as const;

const B2C_FREE_SLUG = 'b2c-free';
const B2C_PLUS_MONTH_SLUG = 'b2c-plus-month';
const B2C_PLUS_6MO_SLUG = 'b2c-plus-6mo';

const FREE_OR_TRIAL_SLUGS = new Set<string>(['trial', B2C_FREE_SLUG]);
const POPULAR_SLUGS = new Set<string>([
  'planner-pro-month',
  'planner-pro-year',
]);

// ---------------------------------------------------------------------------
// Per-plan feature bullets. The migration doesn't store marketing-copy
// features (deliberate — they belong with the UI, not the billing schema),
// so we hand-author them per slug. Quotas are still rendered from the row
// data so price changes don't drift the displayed limits.
// ---------------------------------------------------------------------------

function planFeatures(plan: Plan): string[] {
  const bullets: string[] = [];
  if (plan.active_events_cap === null) {
    bullets.push('Eventos activos ilimitados');
  } else {
    bullets.push(
      `${plan.active_events_cap} ${plan.active_events_cap === 1 ? 'evento activo' : 'eventos activos'}`,
    );
  }
  if (plan.whatsapp_messages_per_period !== null) {
    const suffix = plan.billing_cycle === 'YEAR' ? '/año' : '/periodo';
    bullets.push(
      `${plan.whatsapp_messages_per_period.toLocaleString('es-MX')} mensajes WhatsApp${suffix}`,
    );
  } else {
    bullets.push('Mensajes WhatsApp ilimitados');
  }
  if (plan.storage_bytes !== null) {
    const gb = plan.storage_bytes / 1024 / 1024 / 1024;
    if (gb >= 1) {
      bullets.push(`${gb >= 10 ? Math.round(gb) : gb.toFixed(0)} GB de documentos`);
    } else {
      const mb = Math.round(plan.storage_bytes / 1024 / 1024);
      bullets.push(`${mb} MB de documentos`);
    }
  } else {
    bullets.push('Almacenamiento ilimitado');
  }
  if (plan.users_per_workspace !== null && plan.users_per_workspace > 1) {
    bullets.push(`Hasta ${plan.users_per_workspace} usuarios por workspace`);
  }
  bullets.push('Invitados ilimitados');
  bullets.push('Proveedores ilimitados');
  return bullets;
}

// ---------------------------------------------------------------------------
// "12 for 10" badge — computed from data, so super-admin price edits stay
// truthful. We compare the yearly price to 12× the monthly; anything under
// 11× counts as a savings tile.
// ---------------------------------------------------------------------------

function yearlySavingsLabel(monthly: Plan | null, yearly: Plan | null): string | null {
  if (!monthly || !yearly) return null;
  if (monthly.price_mxn_cents <= 0) return null;
  const ratio = yearly.price_mxn_cents / monthly.price_mxn_cents;
  if (ratio < 11) {
    // 12 months for 10 ≈ 17% off.
    return 'Ahorra 17%';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

const Pricing: React.FC = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [b2cPlusSixMonth, setB2cPlusSixMonth] = useState(false);
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const plansQuery = useQuery({
    queryKey: ['public-plans'],
    queryFn: fetchPublicPlans,
  });

  // One-shot ?checkout=cancelled toast. We track a ref so React strict
  // mode's double-mount doesn't fire it twice in dev.
  const cancelledShownRef = React.useRef(false);
  React.useEffect(() => {
    if (cancelledShownRef.current) return;
    if (searchParams.get('checkout') === 'cancelled') {
      cancelledShownRef.current = true;
      toast({
        title: 'Pago cancelado',
        description: 'Cancelaste el proceso de pago. Vuelve cuando quieras.',
      });
    }
  }, [searchParams, toast]);

  // Lookup map by slug for the grouping render.
  const bySlug = useMemo(() => {
    const map = new Map<string, Plan>();
    for (const plan of plansQuery.data ?? []) {
      map.set(plan.slug, plan);
    }
    return map;
  }, [plansQuery.data]);

  const plannerSlugs = isAnnual ? PLANNER_YEARLY_SLUGS : PLANNER_MONTHLY_SLUGS;
  const plannerPlans = plannerSlugs
    .map((slug) => bySlug.get(slug))
    .filter((p): p is Plan => Boolean(p));

  const b2cFree = bySlug.get(B2C_FREE_SLUG) ?? null;
  const b2cPlusMonth = bySlug.get(B2C_PLUS_MONTH_SLUG) ?? null;
  const b2cPlus6mo = bySlug.get(B2C_PLUS_6MO_SLUG) ?? null;

  const handleCtaClick = async (plan: Plan) => {
    // Free / trial → no Stripe, push to signup/dashboard.
    if (FREE_OR_TRIAL_SLUGS.has(plan.slug)) {
      if (!user) {
        navigate(`/signup?plan=${encodeURIComponent(plan.slug)}`);
      }
      return;
    }
    // Paid plan → require auth, then call edge function.
    if (!user) {
      const nextUrl = `/precios?plan=${encodeURIComponent(plan.slug)}`;
      navigate(`/login?next=${encodeURIComponent(nextUrl)}`);
      return;
    }
    if (busySlug !== null) return;
    setBusySlug(plan.slug);
    try {
      const { data, error } = await supabase.functions.invoke('createCheckoutSession', {
        body: { plan_slug: plan.slug },
      });
      if (error) throw error;
      const result = data as CheckoutSessionResponse | null;
      if (!result?.url) {
        throw new Error('respuesta sin URL de checkout');
      }
      // Use assign so the back button returns to /precios (matches Stripe's
      // recommended redirect pattern for Checkout).
      window.location.assign(result.url);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido.';
      console.error('Pricing: createCheckoutSession failed', err);
      toast({
        variant: 'destructive',
        title: 'No pudimos abrir el pago',
        description: message,
      });
      setBusySlug(null);
    }
  };

  const renderCtaLabel = (plan: Plan): string => {
    if (busySlug === plan.slug) return 'Procesando…';
    if (FREE_OR_TRIAL_SLUGS.has(plan.slug)) {
      return user ? 'Plan actual' : 'Iniciar prueba gratis';
    }
    return 'Elegir plan';
  };

  const yearlySavings = yearlySavingsLabel(
    bySlug.get('planner-pro-month') ?? null,
    bySlug.get('planner-pro-year') ?? null,
  );

  // ----------------------------------------------------------------------
  // FAQ — pure marketing copy, unchanged from the previous iteration.
  // ----------------------------------------------------------------------
  const faqs = [
    {
      question: '¿Puedo cambiar de plan en cualquier momento?',
      answer:
        'Sí, puedes cambiar tu plan cuando quieras. Si cambias a un plan superior, pagarás la diferencia prorrateada.',
    },
    {
      question: '¿Qué pasa si excedo mis mensajes de WhatsApp?',
      answer:
        'Te avisamos antes de llegar al límite. Si necesitas más, puedes subir de plan o esperar al próximo ciclo.',
    },
    {
      question: '¿Necesito tarjeta para probar CoPlanner?',
      answer:
        'No. La prueba dura 15 días con 1 evento activo y no requiere tarjeta. Solo se te pedirá si decides suscribirte.',
    },
    {
      question: '¿Puedo cancelar mi suscripción?',
      answer:
        'Puedes cancelar tu suscripción en cualquier momento desde tu cuenta. Conservas el acceso hasta el final del periodo pagado.',
    },
  ];

  const isLoading = plansQuery.isLoading;
  const isError = plansQuery.isError;
  const isEmpty = !isLoading && !isError && (plansQuery.data?.length ?? 0) === 0;

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      <section className="pt-32 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Planes claros, sin sorpresas
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Elige el plan que mejor se adapte a tus necesidades. Precios en pesos mexicanos.
          </p>

          <div className="flex items-center justify-center space-x-4 mb-4">
            <span className={!isAnnual ? 'text-gray-900' : 'text-gray-500'}>Mensual</span>
            <button
              type="button"
              onClick={() => setIsAnnual((v) => !v)}
              aria-pressed={isAnnual}
              aria-label="Alternar entre precios mensuales y anuales"
              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
              style={{ backgroundColor: isAnnual ? PRIMARY_COLOR : '#e5e7eb' }}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isAnnual ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={isAnnual ? 'text-gray-900' : 'text-gray-500'}>
              Anual{' '}
              {isAnnual && yearlySavings && (
                <span className="text-green-600 text-sm ml-1">({yearlySavings})</span>
              )}
            </span>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* --------- Planner (B2B) tiles --------- */}
          {isLoading ? (
            <div className="grid md:grid-cols-3 gap-8" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border-2 border-gray-200 p-8"
                  data-testid="pricing-skeleton"
                >
                  <Skeleton className="h-6 w-32 mb-4" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-10 w-40 mb-6" />
                  <div className="space-y-3 mb-8">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-4 w-4/6" />
                  </div>
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className="text-center text-red-600">
              No pudimos cargar los planes. Recarga la página o intenta más tarde.
            </div>
          ) : isEmpty ? (
            <div className="text-center max-w-2xl mx-auto bg-gray-50 border border-gray-200 rounded-2xl p-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Estamos actualizando nuestros planes
              </h2>
              <p className="text-gray-600 mb-4">
                Vuelve en unos minutos o escríbenos para asesorarte personalmente.
              </p>
              <a
                href="mailto:contacto@coplaner.mx"
                className="text-brand-primary underline font-medium"
                style={{ color: PRIMARY_COLOR }}
              >
                contacto@coplaner.mx
              </a>
            </div>
          ) : (
            <>
              {plannerPlans.length > 0 && (
                <div className="grid md:grid-cols-3 gap-8">
                  {plannerPlans.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      isPopular={POPULAR_SLUGS.has(plan.slug)}
                      busy={busySlug === plan.slug}
                      ctaLabel={renderCtaLabel(plan)}
                      onCta={() => handleCtaClick(plan)}
                      features={planFeatures(plan)}
                    />
                  ))}
                </div>
              )}

              {/* --------- B2C section --------- */}
              {(b2cFree || b2cPlusMonth || b2cPlus6mo) && (
                <div className="mt-20">
                  <div className="text-center mb-10">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      Para parejas y familias
                    </h2>
                    <p className="text-gray-600">
                      Organiza tu propio evento sin contratar un planner profesional.
                    </p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    {b2cFree && (
                      <PlanCard
                        plan={b2cFree}
                        isPopular={false}
                        busy={false}
                        ctaLabel={renderCtaLabel(b2cFree)}
                        onCta={() => handleCtaClick(b2cFree)}
                        features={planFeatures(b2cFree)}
                      />
                    )}
                    {(b2cPlusMonth || b2cPlus6mo) && (
                      <B2cPlusCard
                        monthly={b2cPlusMonth}
                        sixMonth={b2cPlus6mo}
                        sixMonthSelected={b2cPlusSixMonth}
                        onToggleSixMonth={setB2cPlusSixMonth}
                        busySlug={busySlug}
                        onCta={(plan) => handleCtaClick(plan)}
                        ctaLabel={(plan) => renderCtaLabel(plan)}
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Preguntas frecuentes sobre facturación
          </h2>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border border-gray-200 rounded-lg px-6"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section
        className="py-20 text-white"
        style={{
          background:
            'linear-gradient(90deg, rgba(61, 90, 128, 1) 0%, rgba(61, 110, 128, 1) 50%, rgba(89, 61, 128, 1) 100%)',
        }}
      >
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            ¿Listo para organizar tu siguiente evento?
          </h2>
          <p className="text-xl opacity-90 mb-8">
            Comienza gratis y lleva tu evento al siguiente nivel
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-white hover:bg-gray-100" style={{ color: PRIMARY_COLOR }}>
              Empezar ahora
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Plan card — generic single-tile renderer.
// ---------------------------------------------------------------------------

interface PlanCardProps {
  plan: Plan;
  isPopular: boolean;
  busy: boolean;
  ctaLabel: string;
  onCta: () => void;
  features: string[];
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  isPopular,
  busy,
  ctaLabel,
  onCta,
  features,
}) => {
  const period =
    plan.billing_cycle === 'MONTH'
      ? '/mes'
      : plan.billing_cycle === 'YEAR'
      ? '/año'
      : '';

  return (
    <div
      className={`bg-white rounded-2xl border-2 p-8 relative transition-all duration-200 ${
        isPopular ? 'shadow-xl scale-105' : 'border-gray-200 hover:border-gray-300'
      }`}
      style={{ borderColor: isPopular ? PRIMARY_COLOR : undefined }}
      data-testid={`plan-card-${plan.slug}`}
    >
      {isPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span
            className="text-white px-4 py-1 rounded-full text-sm font-medium"
            style={{ backgroundColor: PRIMARY_COLOR }}
          >
            Más popular
          </span>
        </div>
      )}

      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.display_name}</h3>
        <div className="mb-4">
          <span className="text-4xl font-bold text-gray-900">
            {formatMxn(plan.price_mxn_cents)}
          </span>
          {period && <span className="text-gray-600">{period}</span>}
        </div>
      </div>

      <ul className="space-y-3 mb-8">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-center">
            <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
            <span className="text-gray-700">{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        className={`w-full ${
          isPopular ? 'text-white hover:opacity-90' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
        }`}
        style={isPopular ? { backgroundColor: PRIMARY_COLOR } : {}}
        onClick={onCta}
        disabled={busy}
        aria-label={`${ctaLabel} — ${plan.display_name}`}
        data-testid={`plan-cta-${plan.slug}`}
      >
        {ctaLabel}
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// B2C Plus card — single CTA with a monthly ⇄ 6-month one-time toggle that
// switches which slug the button submits to checkout.
// ---------------------------------------------------------------------------

interface B2cPlusCardProps {
  monthly: Plan | null;
  sixMonth: Plan | null;
  sixMonthSelected: boolean;
  onToggleSixMonth: (next: boolean) => void;
  busySlug: string | null;
  onCta: (plan: Plan) => void;
  ctaLabel: (plan: Plan) => string;
}

const B2cPlusCard: React.FC<B2cPlusCardProps> = ({
  monthly,
  sixMonth,
  sixMonthSelected,
  onToggleSixMonth,
  busySlug,
  onCta,
  ctaLabel,
}) => {
  // Pick the active plan based on the toggle, falling back gracefully if
  // one of the two isn't seeded (e.g. super admin paused the 6-mo offer).
  const active = sixMonthSelected ? sixMonth ?? monthly : monthly ?? sixMonth;
  if (!active) return null;

  const features = planFeatures(active);
  const busy = busySlug === active.slug;

  return (
    <div
      className="bg-white rounded-2xl border-2 border-gray-200 hover:border-gray-300 p-8 relative transition-all duration-200"
      data-testid="plan-card-b2c-plus"
    >
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          {active.display_name}
        </h3>
        <div className="mb-2">
          <span className="text-4xl font-bold text-gray-900">
            {formatMxn(active.price_mxn_cents)}
          </span>
          <span className="text-gray-600">
            {active.billing_cycle === 'MONTH' ? '/mes' : ' pago único'}
          </span>
        </div>
        {sixMonth && monthly && (
          <div className="mt-3 inline-flex items-center gap-2 text-sm bg-gray-50 rounded-full px-3 py-1">
            <button
              type="button"
              onClick={() => onToggleSixMonth(false)}
              className={`px-2 py-0.5 rounded-full transition-colors ${
                !sixMonthSelected
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              aria-pressed={!sixMonthSelected}
            >
              Mensual
            </button>
            <button
              type="button"
              onClick={() => onToggleSixMonth(true)}
              className={`px-2 py-0.5 rounded-full transition-colors ${
                sixMonthSelected
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              aria-pressed={sixMonthSelected}
              data-testid="b2c-plus-6mo-toggle"
            >
              o {formatMxn(sixMonth.price_mxn_cents)} pago único (6 meses)
            </button>
          </div>
        )}
      </div>

      <ul className="space-y-3 mb-8">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-center">
            <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
            <span className="text-gray-700">{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        className="w-full text-white hover:opacity-90"
        style={{ backgroundColor: PRIMARY_COLOR }}
        onClick={() => onCta(active)}
        disabled={busy}
        aria-label={`${ctaLabel(active)} — ${active.display_name}`}
        data-testid={`plan-cta-${active.slug}`}
      >
        {ctaLabel(active)}
      </Button>
    </div>
  );
};

export default Pricing;
